/**
 * «Профиль» — экран няни.
 *
 * Две вещи, которые здесь по-настоящему важны: реквизиты, по которым придут
 * деньги, и честный статус геометок. Всё остальное — контекст.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { activeMembership, useFamily, useSession } from '@/app/session'
import { FeedbackSheet } from '@/features/feedback/FeedbackSheet'
import { IncidentSheet } from '@/features/incidents/IncidentSheet'
import { useTrust } from '@/shared/api/trust'
import { useUpdateProfile } from '@/shared/api/mutations'
import { formatMoney } from '@/shared/format'
import { addToHomeScreenIfSupported } from '@/shared/telegram'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Screen } from '@/shared/ui/Screen'
import { Field, TextInput } from '@/shared/ui/fields'
import { Avatar, Card, Chip, Divider, Money, Row, SectionTitle } from '@/shared/ui/primitives'
import { EmptyState, ErrorBanner } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function ProfileScreen() {
  const me = useSession((state) => state.me)
  const family = useFamily()
  const membership = useSession(activeMembership)

  const [incidentOpen, setIncidentOpen] = useState(false)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const navigate = useNavigate()
  const familyId = family?.id ?? 0
  const trust = useTrust(familyId)

  const [phone, setPhone] = useState(me?.user.phone ?? '')
  const [bank, setBank] = useState(me?.user.bank_hint ?? '')

  const save = useUpdateProfile({
    onSuccess: () => toast.success('Реквизиты сохранены'),
  })

  if (!me || !family || !membership) {
    return <EmptyState icon="role-nanny" title="Профиль недоступен" />
  }

  const geoOn = family.settings?.geo_enabled ?? false
  const dirty = phone !== (me.user.phone ?? '') || bank !== (me.user.bank_hint ?? '')

  return (
    <Screen title="Профиль" subtitle={family.title}>
      {save.error && <ErrorBanner message={save.error.message} />}

      <Card className="flex items-center gap-3">
        <Avatar name={me.user.first_name} src={me.user.photo_url} size={52} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-lead font-semibold">
            {me.user.first_name} {me.user.last_name ?? ''}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Chip tone="accent">Няня</Chip>
            {geoOn && (
              <Chip tone={me.geo_consent_granted ? 'success' : 'neutral'} dot>
                {me.geo_consent_granted ? 'геометки включены' : 'геометки выключены'}
              </Chip>
            )}
          </div>
        </div>
      </Card>

      {/* Условия работы */}
      <Card>
        <SectionTitle>Условия</SectionTitle>
        <div className="flex items-baseline justify-between py-1">
          <span className="text-text-muted text-body">Ставка</span>
          {membership.hourly_rate ? (
            <span className="flex items-baseline gap-1">
              <Money value={membership.hourly_rate} currency={family.currency} size="lg" />
              <span className="text-text-muted text-caption">/ час</span>
            </span>
          ) : (
            <span className="text-text-muted text-body">не задана</span>
          )}
        </div>

        {membership.overtime_after_min ? (
          <>
            <Divider className="my-1" />
            <div className="flex items-baseline justify-between py-1">
              <span className="text-text-muted text-body">
                Переработка после {Math.round(membership.overtime_after_min / 60)} ч
              </span>
              <span className="text-body font-medium">
                {formatMoney(membership.overtime_rate, family.currency)} / час
              </span>
            </div>
          </>
        ) : null}

        <Divider className="my-1" />
        <div className="flex items-baseline justify-between py-1">
          <span className="text-text-muted text-body">Округление смены</span>
          <span className="text-body font-medium">до {membership.rounding_min} мин</span>
        </div>
      </Card>

      {/* Реквизиты */}
      <Card className="space-y-4">
        <SectionTitle>Куда переводить</SectionTitle>
        <Field
          label="Телефон для переводов"
          hint="Родитель увидит номер на экране оплаты и сможет скопировать его в один тап."
        >
          <TextInput
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            inputMode="tel"
            placeholder="+7 900 123-45-67"
            maxLength={32}
          />
        </Field>
        <Field label="Банк" hint="Чтобы родитель не гадал, куда именно уйдёт перевод.">
          <TextInput
            value={bank}
            onChange={(event) => setBank(event.target.value)}
            placeholder="Сбер, Т-Банк"
            maxLength={64}
          />
        </Field>
        {dirty && (
          <Button
            block
            loading={save.isPending}
            onClick={() => save.mutate({ phone: phone || null, bank_hint: bank || null })}
          >
            Сохранить
          </Button>
        )}
      </Card>

      {/* Честность про геометки — на экране няни это важнее всего */}
      <Card>
        <SectionTitle>Геометки</SectionTitle>
        {geoOn ? (
          <p className="text-text-muted text-note leading-snug">
            Родитель включил геометки для семьи. Точка сохраняется только в момент, когда вы
            сами нажимаете кнопку — «Начать смену», «Я на месте», «Забрала». Фонового
            отслеживания нет. Согласие отзывается в один тап, и родитель получит нейтральное
            уведомление без обвинений.
          </p>
        ) : (
          <p className="text-text-muted text-note leading-snug">
            Геометки в этой семье выключены. Ваше местоположение не запрашивается вообще.
          </p>
        )}
      </Card>

      {/* Центр доверия — тот же экран, что у родителей */}
      <Card padded={false} className="px-4">
        <Row
          icon={
            <span
              aria-hidden
              className="bg-surface-muted flex size-10 items-center justify-center rounded-[var(--radius-button)]"
            >
              <Icon name="trust" size={18} />
            </span>
          }
          title="Доверие"
          subtitle="Кто видит данные, что менялось, происшествия"
          right={
            trust.data && trust.data.needs_attention > 0 ? (
              <Chip tone="warm">{trust.data.needs_attention}</Chip>
            ) : (
              <Icon name="action-chevron" size={21} />
            )
          }
          onClick={() => navigate('/trust')}
        />
      </Card>

      {/* Кнопка происшествия живёт здесь, а не на главном экране: не пугать
          без повода, но чтобы искать её не приходилось. Значок — отдельным
          элементом, иначе скринридер прочитает его как часть подписи. */}
      <Button
        block
        variant="secondary"
        icon={<Icon name="incident" size={20} />}
        onClick={() => setIncidentOpen(true)}
      >
        Сообщить о происшествии
      </Button>

      {/* Закрытая бета: у няни канал обратной связи не менее важен, чем
          у родителя — она пользуется приложением каждый день. */}
      <Button
        block
        variant="secondary"
        icon={<Icon name="feedback" size={20} />}
        onClick={() => setFeedbackOpen(true)}
      >
        Идея или проблема
      </Button>

      <Button
        block
        variant="ghost"
        onClick={() => {
          if (!addToHomeScreenIfSupported()) {
            toast.show('Ваш Telegram пока не умеет добавлять на экран')
          }
        }}
      >
        Добавить на экран телефона
      </Button>

      <IncidentSheet
        open={incidentOpen}
        familyId={familyId}
        onClose={() => setIncidentOpen(false)}
      />
      <FeedbackSheet open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </Screen>
  )
}
